import { useMemo, useState } from "react";
import { useGetList, useGetMany, useTranslate } from "ra-core";
import { MessageSquare } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useIsMobile } from "@/hooks/use-mobile";

import MobileHeader from "../layout/MobileHeader";
import { useRelativeDate } from "../misc/RelativeDate";
import type { Contact, Sale } from "../types";
import type { WhatsAppMessage } from "@/types/realEstate";
import { Avatar } from "./Avatar";
import { ContactTelephonyDrawer } from "./ContactTelephonyDrawer";
import { isCampaignWhatsAppMessage } from "./contactModel";

const UNREAD_WA_FILTER = {
  direction: "INBOUND",
  "status@eq": "unread",
} as const;

type InboxThread = {
  contactId: string;
  lastMessage: WhatsAppMessage;
};

function ThreadRow({
  thread,
  contact,
  agent,
  onOpen,
}: {
  thread: InboxThread;
  contact?: Contact;
  agent?: Sale;
  onOpen: () => void;
}) {
  const relativeTime = useRelativeDate(thread.lastMessage.created_at);
  const name = contact
    ? `${contact.first_name} ${contact.last_name ?? ""}`.trim()
    : `Contact #${thread.contactId}`;

  return (
    <button
      type="button"
      onClick={onOpen}
      className="w-full flex items-center gap-3 p-3 text-left hover:bg-muted transition-colors"
    >
      <Avatar record={contact} width={40} height={40} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className="font-medium truncate">{name}</span>
          <span className="text-xs text-muted-foreground shrink-0">
            {relativeTime}
          </span>
        </div>
        <p className="text-sm text-muted-foreground truncate">
          {thread.lastMessage.message_body}
        </p>
        {agent ? (
          <p className="text-[11px] text-muted-foreground mt-0.5">
            Agent: {`${agent.first_name} ${agent.last_name}`.trim()}
          </p>
        ) : null}
      </div>
      <Badge className="bg-emerald-600 text-white shrink-0">Unread</Badge>
    </button>
  );
}

export const InboxList = () => {
  const isMobile = useIsMobile();
  const translate = useTranslate();
  const [drawerContactId, setDrawerContactId] = useState<
    string | number | null
  >(null);

  const { data: unreadMessages, isPending } = useGetList<WhatsAppMessage>(
    "whatsapp_messages",
    {
      filter: UNREAD_WA_FILTER,
      sort: { field: "created_at", order: "DESC" },
      pagination: { page: 1, perPage: 200 },
    },
  );

  const threads = useMemo<InboxThread[]>(() => {
    const seen = new Set<string>();
    const rows: InboxThread[] = [];
    for (const message of unreadMessages ?? []) {
      if (isCampaignWhatsAppMessage(message)) continue;
      if (message.contact_id == null) continue;
      const contactId = String(message.contact_id);
      if (seen.has(contactId)) continue;
      seen.add(contactId);
      rows.push({ contactId, lastMessage: message });
    }
    return rows;
  }, [unreadMessages]);

  const contactIds = useMemo(
    () => threads.map((thread) => thread.contactId),
    [threads],
  );
  const { data: contacts } = useGetMany<Contact>(
    "contacts",
    { ids: contactIds },
    { enabled: contactIds.length > 0 },
  );
  const { data: sales } = useGetList<Sale>("sales", {
    pagination: { page: 1, perPage: 200 },
  });

  const contactById = useMemo(() => {
    const map = new Map<string, Contact>();
    (contacts ?? []).forEach((c) => map.set(String(c.id), c));
    return map;
  }, [contacts]);

  const salesById = useMemo(() => {
    const map = new Map<string, Sale>();
    (sales ?? []).forEach((s) => map.set(String(s.id), s));
    return map;
  }, [sales]);

  const activeContact =
    drawerContactId != null
      ? contactById.get(String(drawerContactId))
      : undefined;
  const activeContactName = activeContact
    ? `${activeContact.first_name} ${activeContact.last_name ?? ""}`.trim()
    : "";
  const activeContactPhone = activeContact?.phone_jsonb?.[0]?.number ?? "";

  const content = (
    <div className="max-w-3xl mx-auto p-4 md:p-6 space-y-4">
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <MessageSquare className="w-6 h-6 text-emerald-600" />
          {translate("resources.contacts.inbox.title", { _: "Inbox" })}
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          {translate("resources.contacts.inbox.subtitle", {
            _: "1:1 WhatsApp replies awaiting a response. Campaign broadcasts are excluded.",
          })}
        </p>
      </div>

      {isPending ? (
        <p className="text-sm text-muted-foreground py-12 text-center">
          {translate("crm.common.loading", { _: "Loading…" })}
        </p>
      ) : threads.length === 0 ? (
        <p className="text-sm text-muted-foreground py-12 text-center">
          {translate("resources.contacts.inbox.empty", {
            _: "No unread WhatsApp messages. Nice work!",
          })}
        </p>
      ) : (
        <div className="divide-y rounded-lg border bg-card">
          {threads.map((thread) => (
            <ThreadRow
              key={thread.contactId}
              thread={thread}
              contact={contactById.get(thread.contactId)}
              agent={
                contactById.get(thread.contactId)?.sales_id != null
                  ? salesById.get(
                      String(contactById.get(thread.contactId)!.sales_id),
                    )
                  : undefined
              }
              onOpen={() => setDrawerContactId(thread.contactId)}
            />
          ))}
        </div>
      )}

      <ContactTelephonyDrawer
        open={drawerContactId != null}
        onOpenChange={(open) => {
          if (!open) setDrawerContactId(null);
        }}
        contactName={activeContactName}
        contactPhone={activeContactPhone}
        contactId={drawerContactId ?? undefined}
        contact={activeContact}
        initialTab="whatsapp"
      />
    </div>
  );

  if (isMobile) {
    return (
      <>
        <MobileHeader>
          <h1 className="text-xl font-semibold truncate">
            {translate("resources.contacts.inbox.title", { _: "Inbox" })}
          </h1>
        </MobileHeader>
        {content}
      </>
    );
  }

  return content;
};

export default InboxList;
